import React, { forwardRef } from 'react';
import { cva } from '@landr/styled-system/css';
import { Button, PaginationNavigationProps, Flex } from '@landr/maestro';
import { useLocation, Link } from 'react-router-dom';
import { getPartOfCurrentQueryString, combineQueryStrings } from 'helpers';
import { AudioConfigParams, NavigationParams } from 'types/params';

const getQueryWithPagination = ({
    search,
    page,
}: {
    search: string;
    page?: number;
}) => {
    // TODO - https://mixgenius.atlassian.net/browse/SP-10142
    const clearedQueryString = getPartOfCurrentQueryString({
        routerQueryString: search,
        queriesToExclude: [NavigationParams.page, AudioConfigParams.autoPlay],
    });

    const queryString = combineQueryStrings(
        clearedQueryString,
        `${NavigationParams.page}=${page}`,
    );

    return page ? `?${queryString}` : search;
};

type NavigationItemProps = PaginationNavigationProps & {
    page: number;
    currentPage?: number;
    // Add any other specific props you need
};

export const navigationItemStyles = cva({
    base: {
        width: 'sm',
        height: 'sm',
        justifyContent: 'center',
        alignItems: 'center',
        marginX: '2px',
        transition: 'background-color 0.2s ease-in-out',
        ':hover > &': {
            backgroundColor: 'surface.neutral.medium.hover',
        },
    },
    variants: {
        isCurrentPage: {
            true: {
                backgroundColor: 'surface.neutral.low.active',
                color: 'text.high',
                ':hover > &': {
                    backgroundColor: 'surface.neutral.medium.hover',
                },
            },
        },
    },
});

const NavigationItem = forwardRef<HTMLAnchorElement, NavigationItemProps>(
    (props, ref) => {
        const { children, page, isDisabled, size, currentPage, ...rest } =
            props;
        const { search } = useLocation();
        const queryString = getQueryWithPagination({ search, page });
        const isCurrentPage = page === currentPage;

        if (isDisabled) {
            return (
                <Button size="sm" isDisabled={isDisabled} {...rest}>
                    {children}
                </Button>
            );
        }

        const scrollToTop = () => {
            const element = document.querySelector('#product-header-wrapper');

            element &&
                element.scrollIntoView({
                    behavior: 'smooth',
                });
        };

        const { className, 'aria-label': ariaLabel } = rest;

        return (
            <Link
                ref={ref}
                to={queryString}
                className={className}
                aria-label={ariaLabel}
                onClick={scrollToTop}
            >
                <Flex className={navigationItemStyles({ isCurrentPage })}>
                    {children}
                </Flex>
            </Link>
        );
    },
);

export default NavigationItem;
